import { pdsTv } from '@nestle-npus-nbm/pds-ui';
import { Link as GatsbyLink } from 'gatsby';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CMS_THEME } from 'src/common/enums';
import { Link } from '../Link';
import BazaarvoiceWidget from '../bazaarvoice/BazaarvoiceWidget';
import { CmsButtonLink } from '../button/CmsButton';
import PricespiderWidget from '../pricespider/PricespiderWidget';
import ProductStatus from '../product/ProductStatus';

const body = pdsTv({
  slots: {
    imageLink:
      'pds-box-border pds-block pds-rounded-md pds-px-0 pds-py-5.5 pds-text-center md:pds-px-5.5 [&_img]:pds-mix-blend-multiply ',
    brand: 'pds-mt-4 pds-text-body-xs pds-uppercase pds-tracking-wider',
  },
  // TODO: discuss with mike
  variants: {
    theme: {
      light: {
        imageLink: 'pds-bg-surface-layer',
        brand: 'pds-text-neutral',
      },
      dark: {
        imageLink: 'pds-bg-surface-layer',
        brand: 'pds-text-surface-text',
      },
      neutral: {
        imageLink: 'pds-bg-surface',
        brand: 'pds-text-neutral-text',
      },
    },
  },
});

interface Props {
  image: IGatsbyImageData;
  link: string;
  title: string;
  psID: string;
  bvID: string;
  cmsTheme: CMS_THEME;
  brand?: string;
  onClick?: () => void;
  cardType?: string;
  outlinedCta?: boolean;
  linkDirect?: { title?: string; uri: string };
  productStatuses?: IFieldProductStatus[];
  productVariations?: {
    field_item_description: string;
    field_item_quantity: number;
    field_item_size: number;
    field_short_description: string;
    field_upc_code: string;
  }[];
}

const ProductCardBody: React.FC<Props> = ({
  cmsTheme = CMS_THEME.LIGHT,
  link,
  image,
  onClick,
  brand,
  title,
  psID,
  bvID,
  cardType,
  outlinedCta = false,
  linkDirect = undefined,
  productVariations = [],
  productStatuses = [],
}) => {
  const { t } = useTranslation();
  const isExternalLink = link.includes('http');
  const { imageLink, brand: brandStyles } = useMemo(() => body({ theme: cmsTheme }), [cmsTheme]);

  const determineCta = () => {
    if (linkDirect?.uri) {
      return (
        <CmsButtonLink
          className="pds-box-border pds-w-full"
          cmsTheme={cmsTheme}
          onClick={onClick}
          variant={outlinedCta ? 'btn-outline' : 'btn'}
          to={linkDirect.uri}
          target="_blank"
          aria-label={`${linkDirect.title} ${title}`}
        >
          {linkDirect.title ?? 'Buy Direct'}
        </CmsButtonLink>
      );
    }

    return (
      <PricespiderWidget
        title={title}
        cmsTheme={cmsTheme}
        id={psID}
        outlined={cmsTheme === CMS_THEME.DARK ? true : outlinedCta}
      />
    );
  };

  const hasStatuses = productStatuses.length > 0;

  return (
    <>
      <GatsbyLink
        to={link}
        onClick={onClick}
        className={imageLink()}
        target={isExternalLink ? '_blank' : undefined}
        aria-label={title}
      >
        <GatsbyImage image={image} objectFit="contain" alt="" style={{ maxHeight: '300px' }} />
      </GatsbyLink>
      {brand && <div className={brandStyles()}>{brand}</div>}
      {hasStatuses ? (
        <div className="pds-mt-4">
          <ProductStatus statuses={productStatuses} title={title} />
        </div>
      ) : null}
      <Link
        theme={cmsTheme}
        to={link}
        onClick={onClick}
        target={isExternalLink ? '_blank' : undefined}
        analytics={false}
        tabIndex={-1}
        className="pds-mt-4 pds-text-body-md pds-font-bold pds-no-underline hover:pds-underline focus:pds-underline active:pds-underline"
        aria-hidden
        id={psID}
      >
        {title}
      </Link>
      {cardType === 'products' && (
        <div className="pds-mt-4">
          <BazaarvoiceWidget attributes={{ productID: bvID, widgetID: 'inline_rating' }} />
        </div>
      )}
      {productVariations.length > 1 ? (
        <div className="pds-mt-4">
          <span className="pds-text-body-md pds-font-normal pds-text-surface-text">
            {t('{{count}} sizes available', { count: productVariations.length })}
          </span>
        </div>
      ) : null}
      {/* This is a spacer to make the card body grow to fill the space */}
      <div className="pds-grow" />
      <div className="pds-mt-5">{determineCta()}</div>
    </>
  );
};

export default ProductCardBody;
